import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { LogOut, Search, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminListEnquiries, adminEnquiryStats, adminUpdateEnquiryStatus } from "@/lib/api";
import { LEAD_STATUSES, LEAD_STATUS_CLASSES, TIER_LABELS, formatINR } from "@/lib/format";

const PAYMENT_STATUS_CLASSES = {
  paid: "bg-emerald-50 text-emerald-900 border border-emerald-100",
  pending: "bg-amber-50 text-amber-900 border border-amber-100",
  failed: "bg-rose-50 text-rose-900 border border-rose-100",
  not_applicable: "bg-slate-100 text-slate-600 border border-slate-200",
};

const PAYMENT_STATUS_LABELS = {
  paid: "Deposit Paid",
  pending: "Payment Pending",
  failed: "Payment Failed",
  not_applicable: "N/A",
};

export default function AdminDashboard() {
  const { username, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, statData] = await Promise.all([
        adminListEnquiries({ status: statusFilter, search: search || undefined }),
        adminEnquiryStats(),
      ]);
      setEnquiries(list);
      setStats(statData);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  async function handleStatusChange(id, newStatus) {
    setUpdatingId(id);
    try {
      await adminUpdateEnquiryStatus(id, newStatus);
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, lead_status: newStatus } : e)));
      toast.success("Lead status updated");
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  }

  function handleLogout() {
    logout();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Best Escape Holidays" className="h-9 w-auto" />
            <div>
              <h1 className="font-display text-lg font-semibold text-slate-900">Leads Dashboard</h1>
              <p className="text-xs text-slate-500">Logged in as {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="outline" size="sm">View Website</Button></Link>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Card className="p-4">
            <p className="text-xs text-slate-500">Total Leads</p>
            <p className="font-display text-2xl font-bold text-slate-900">{stats.total ?? "-"}</p>
          </Card>
          {LEAD_STATUSES.map((s) => (
            <Card key={s} className="p-4">
              <p className="text-xs text-slate-500">{s}</p>
              <p className="font-display text-2xl font-bold text-slate-900">{stats[s] ?? 0}</p>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="admin-leads-search-input"
              placeholder="Search by name, phone or tour..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-leads-status-filter" className="w-full sm:w-56">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {LEAD_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center p-10">
              <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-primary))]" />
            </div>
          ) : enquiries.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No leads found.</div>
          ) : (
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Received</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Tour</TableHead>
                  <TableHead>Travel Date</TableHead>
                  <TableHead>Travellers</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.map((enq) => (
                  <TableRow key={enq.id} data-testid="admin-leads-row">
                    <TableCell className="whitespace-nowrap text-xs text-slate-500">
                      {dayjs(enq.submission_timestamp).format("DD MMM, HH:mm")}
                    </TableCell>
                    <TableCell className="font-medium text-slate-800">{enq.guest_name}</TableCell>
                    <TableCell>{enq.contact_number}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{enq.tour_title}</TableCell>
                    <TableCell className="whitespace-nowrap">{enq.travel_dates}</TableCell>
                    <TableCell>{enq.total_travellers}</TableCell>
                    <TableCell>{enq.selected_tier ? TIER_LABELS[enq.selected_tier] || enq.selected_tier : "-"}</TableCell>
                    <TableCell className="text-xs">{enq.inquiry_type}</TableCell>
                    <TableCell data-testid="admin-lead-payment-status">
                      <Badge className={`${PAYMENT_STATUS_CLASSES[enq.payment_status || "not_applicable"]} whitespace-nowrap`}>
                        {PAYMENT_STATUS_LABELS[enq.payment_status || "not_applicable"]}
                      </Badge>
                      {enq.payment_status === "paid" && enq.amount_paid ? (
                        <p className="mt-1 text-xs font-medium text-slate-600">{formatINR(enq.amount_paid)}</p>
                      ) : enq.deposit_amount ? (
                        <p className="mt-1 text-xs text-slate-400">{formatINR(enq.deposit_amount)}</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={enq.lead_status}
                        onValueChange={(v) => handleStatusChange(enq.id, v)}
                        disabled={updatingId === enq.id}
                      >
                        <SelectTrigger data-testid="admin-lead-status-dropdown" className="h-8 w-36 text-xs">
                          <Badge className={`${LEAD_STATUS_CLASSES[enq.lead_status]} pointer-events-none`}>{enq.lead_status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => setSelected(enq)} data-testid="admin-lead-view-button">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="admin-lead-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.guest_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <Row label="Tour" value={selected.tour_title} />
                {selected.desired_destination && <Row label="Destination" value={selected.desired_destination} />}
                <Row label="Contact" value={selected.contact_number} />
                <Row label="Travel Date" value={selected.travel_dates} />
                <Row label="Travellers" value={selected.total_travellers} />
                <Row label="Tier" value={selected.selected_tier ? TIER_LABELS[selected.selected_tier] || selected.selected_tier : "Not selected"} />
                <Row label="Enquiry Type" value={selected.inquiry_type} />
                <Row label="Payment Status" value={PAYMENT_STATUS_LABELS[selected.payment_status || "not_applicable"]} />
                {selected.deposit_amount ? <Row label="Deposit Amount" value={formatINR(selected.deposit_amount)} /> : null}
                {selected.amount_paid ? <Row label="Amount Paid" value={formatINR(selected.amount_paid)} /> : null}
                <Row label="Estimated Price" value={selected.estimated_price ? formatINR(selected.estimated_price) : "Price on Request"} />
                <Row label="Status" value={selected.lead_status} />
                <Row label="Received" value={dayjs(selected.submission_timestamp).format("DD MMM YYYY, HH:mm")} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}
